import { useEffect, useRef } from 'react'

// Map of key -> handler. Keys are lowercased; use 'ArrowUp'/'ArrowDown'/'Enter'/'Space'
// (we normalize ' ' to 'Space').
export type ShortcutMap = Record<string, (e: KeyboardEvent) => void>

function normalizeKey(e: KeyboardEvent): string {
  if (e.key === ' ') return 'Space'
  if (e.key.startsWith('Arrow')) return e.key
  if (e.key === 'Enter' || e.key === 'Escape') return e.key
  return e.key.toLowerCase()
}

function isTypingTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false
  if (t.isContentEditable) return true
  const tag = t.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

interface Props {
  shortcuts: ShortcutMap
  enabled?: boolean
}

export function KeyboardShortcuts({ shortcuts, enabled = true }: Props) {
  const latest = useRef(shortcuts)
  latest.current = shortcuts

  useEffect(() => {
    if (!enabled) return
    const handler = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return
      if (e.ctrlKey || e.metaKey || e.altKey) return
      const k = normalizeKey(e)
      const fn = latest.current[k]
      if (fn) {
        e.preventDefault()
        fn(e)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [enabled])

  return null
}

export function ShortcutHint({ keys }: { keys: Array<[string, string]> }) {
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-500">
      {keys.map(([k, label]) => (
        <span key={k} className="flex items-center gap-1">
          <kbd className="rounded border border-slate-700 bg-slate-900 px-1.5 py-0.5 font-mono text-[10px] text-slate-300">
            {k}
          </kbd>
          {label}
        </span>
      ))}
    </span>
  )
}
